/**
 * Aanvragen datacorrectie: leveranciers passen via een formulier hun gegevens aan; hier
 * zie je per aanvraag wat er verandert en neem je het over ("Verwerken"). De details
 * (IBAN, adres, noodcontact) worden live uit Airtable gelezen, niet bewaard in de spiegel.
 * IBAN en allergieën staan gemaskeerd.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Drawer, IconButton, Tab, Table, TableBody, TableCell,
  TableHead, TableRow, Tabs, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { bevestig } from '../../utils/bevestig';
import { meldFout } from '../../utils/apiFout';
import { datumNl } from './hrRekenen';
import { ACCENT, INK, haalJson } from './hrUi';

const API = `${BACKEND_URL}/Api/leveranciers/datacorrecties`;

function Detail({ id, magBewerken, onSluit, onVerwerkt }) {
  const [d, setD] = useState(null);
  const [fout, setFout] = useState(null);
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    let weg = false;
    setD(null); setFout(null);
    haalJson(`${API}/${id}`, 'Aanvraag laden').then((x) => { if (!weg) setD(x); }).catch((e) => { if (!weg) setFout(e.message); });
    return () => { weg = true; };
  }, [id]);

  const verwerk = async () => {
    const n = d.verschillen.length;
    if (!(await bevestig({
      titel: 'Datacorrectie verwerken?',
      tekst: n
        ? `${n} veld${n === 1 ? '' : 'en'} van ${d.naam} worden in Airtable overschreven met de nieuwe waarden, en de aanvraag gaat op 'Verwerkt'. Terugdraaien kan alleen met de hand in Airtable.`
        : `Er verandert niets aan de gegevens van ${d.naam}; de aanvraag gaat op 'Verwerkt'.`,
      knop: 'Verwerken',
    }))) return;
    setBezig(true);
    try {
      const r = await haalJson(`${API}/${id}/verwerken`, 'Verwerken', { method: 'POST' });
      toast.success(`Verwerkt${r.overgeslagen?.length ? ` — niet overgenomen: ${r.overgeslagen.join('; ')}` : ''}`);
      onVerwerkt(id);
    } catch (e) {
      meldFout(e, 'Verwerken');
    } finally {
      setBezig(false);
    }
  };

  return (
    <Box sx={{ width: { xs: '100vw', sm: 520 }, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 18, flex: 1 }}>{d?.naam || 'Aanvraag'}</Typography>
        <IconButton onClick={onSluit} aria-label="Sluiten"><CloseIcon /></IconButton>
      </Box>
      {fout && <Alert severity="error">{fout}</Alert>}
      {!d && !fout && <CircularProgress size={24} />}
      {d && (
        <>
          <Typography sx={{ fontSize: 13, color: INK.zacht, mb: 2 }}>
            Status: {d.status}{d.bewaren ? ` · ${d.bewaren}` : ''}
          </Typography>
          {d.verschillen.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: INK.zacht, mb: 2 }}>Geen verschillen met de huidige gegevens.</Typography>
          ) : (
            <Table size="small" sx={{ mb: 2, '& th': { color: INK.zacht, fontWeight: 700, fontSize: 12 }, '& td': { fontSize: 13, verticalAlign: 'top' } }}>
              <TableHead><TableRow><TableCell>Veld</TableCell><TableCell>Nu</TableCell><TableCell>Wordt</TableCell></TableRow></TableHead>
              <TableBody>
                {d.verschillen.map((v) => (
                  <TableRow key={v.veld}>
                    <TableCell sx={{ color: INK.zacht }}>{v.veld}</TableCell>
                    <TableCell sx={{ color: INK.stil }}>{v.huidig}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: INK.tekst }}>{v.nieuw}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {d.overgeslagen?.length > 0 && (
            <Typography sx={{ fontSize: 12, color: INK.zacht, mb: 2 }}>Wordt niet overgenomen (onbekende keuze): {d.overgeslagen.join('; ')}</Typography>
          )}
          {d.opmerkingen && <Typography sx={{ fontSize: 13, mb: 2, whiteSpace: 'pre-wrap' }}><b>Opmerking:</b> {d.opmerkingen}</Typography>}
          {!d.gekoppeld && <Alert severity="info" sx={{ mb: 2 }}>Deze aanvraag hangt aan geen leverancier — koppel hem eerst in Airtable.</Alert>}
          {['Nog verwijderen', 'Verwijderd'].includes(d.status) && (
            <Alert severity="info" sx={{ mb: 2 }}>Deze leverancier vraagt om verwijderen van zijn gegevens — dat handel je af in Airtable.</Alert>
          )}
          {magBewerken && d.kanVerwerken && (
            d.schrijvenAan ? (
              <Button variant="contained" onClick={verwerk} disabled={bezig} sx={{ textTransform: 'none', bgcolor: ACCENT.color }}>
                {bezig ? 'Bezig…' : 'Verwerken'}
              </Button>
            ) : (
              <Typography sx={{ fontSize: 12, color: INK.stil }}>Schrijven naar Airtable staat uit op test — verwerken kan alleen in productie.</Typography>
            )
          )}
        </>
      )}
    </Box>
  );
}

export default function Datacorrecties({ magBewerken }) {
  const [d, setD] = useState(null);
  const [fout, setFout] = useState(null);
  const [tab, setTab] = useState('open');
  const [open, setOpen] = useState(null);

  const laad = useCallback(async () => {
    setFout(null);
    try { setD(await haalJson(API, 'Datacorrecties laden')); }
    catch (e) { setFout(e.message); }
  }, []);
  useEffect(() => { laad(); }, [laad]);

  const alle = useMemo(() => d?.aanvragen || [], [d]);
  const teDoen = useMemo(() => alle.filter((a) => a.status === 'Nog verwerken'), [alle]);
  const lijst = tab === 'open' ? teDoen : alle;

  if (fout) return <Alert severity="error" action={<Button color="inherit" size="small" onClick={laad}>Opnieuw</Button>}>{fout}</Alert>;
  if (!d) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <>
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2, minHeight: 36, '& .MuiTab-root': { textTransform: 'none', minHeight: 36, fontWeight: 600 } }}>
        <Tab value="open" label={`Nog verwerken (${teDoen.length})`} />
        <Tab value="alle" label={`Alle (${alle.length})`} />
      </Tabs>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ '& th': { color: INK.zacht, fontWeight: 700, fontSize: 12 }, '& td': { fontSize: 13, color: INK.tekst } }}>
          <TableHead><TableRow><TableCell>Leverancier</TableCell><TableCell>Ontvangen</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
          <TableBody>
            {lijst.map((a) => (
              <TableRow key={a.id} hover onClick={() => setOpen(a.id)} sx={{ cursor: 'pointer' }}>
                <TableCell sx={{ fontWeight: 600 }}>{a.naam}</TableCell>
                <TableCell>{datumNl(a.ontvangen)}</TableCell>
                <TableCell>{a.status}</TableCell>
              </TableRow>
            ))}
            {lijst.length === 0 && (
              <TableRow><TableCell colSpan={3} sx={{ color: INK.zacht }}>{tab === 'open' ? 'Niets te verwerken.' : 'Nog geen aanvragen.'}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      <Drawer anchor="right" open={!!open} onClose={() => setOpen(null)}>
        {open && (
          <Detail id={open} magBewerken={magBewerken} onSluit={() => setOpen(null)}
            onVerwerkt={(id) => {
              setD((x) => ({ ...x, aanvragen: x.aanvragen.map((a) => (a.id === id ? { ...a, status: 'Verwerkt' } : a)) }));
              setOpen(null);
            }} />
        )}
      </Drawer>
    </>
  );
}
